
import Vue from 'vue';
import ReferenceService from '@/services/ReferenceService';
import EntityMetaDialog from '@/components/misc/EntityMetaDialog.vue';
import DetailDialog from '@/components/services/reference/DetailDialog.vue';

type HistoryItem = {
  id: string;
  revisionTime: string | null;
  revisionType: string | null;
  payload: Record<string, any>;
  uniqueKey: string;
  diff?: Array<{ key: string; before: any; after: any }>;
};

export default Vue.extend({
  name: 'ReferenceHistoryDialog',
  components: { EntityMetaDialog, DetailDialog },
  props: {
    tenantId: { type: String, required: true },
    templateId: { type: String, required: true },
    entityId: { type: String, required: true },
    external: { type: Boolean, default: false },
  },
  data: () => ({
    localdialog: false,
    loading: false,
    errorMessage: '',
    historyRows: [] as HistoryItem[],
    metaEntity: {},
    headers: [
      { text: 'Time', value: 'revisionTime' },
      { text: 'Type', value: 'revisionType' },
      { text: '', value: 'data-table-expand' },
    ],
  }),
  methods: {
    formatTime(timestamp: string | null) {
      if (!timestamp) return '-';
      try {
        return new Date(timestamp).toLocaleString();
      } catch {
        return timestamp;
      }
    },
    typeColor(revisionType: string | null) {
      if (revisionType === 'create') return 'success';
      if (revisionType === 'update') return 'info';
      if (revisionType === 'delete') return 'error';
      return 'grey';
    },
    buildDiff(
      previousPayload: Record<string, any>,
      currentPayload: Record<string, any>
    ) {
      const allKeys = new Set([
        ...Object.keys(previousPayload || {}),
        ...Object.keys(currentPayload || {}),
      ]);
      const differences: Array<{ key: string; before: any; after: any }> = [];
      allKeys.forEach((key) => {
        const beforeValue = previousPayload[key];
        const afterValue = currentPayload[key];
        if (JSON.stringify(beforeValue) !== JSON.stringify(afterValue)) {
          differences.push({ key, before: beforeValue, after: afterValue });
        }
      });
      return differences;
    },
    async fetchHistory() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const historyItems = await ReferenceService.getSingleDataHistory(
          this.tenantId,
          this.templateId,
          this.entityId
        );
        this.metaEntity = {
          uuid: this.entityId
        };
        const sortedHistory = historyItems
          .map((historyItem) => ({
            ...historyItem,
            payload: historyItem.payload || {},
          }))
          .sort(
            (first, second) =>
              new Date(second.revisionTime || 0).getTime() -
              new Date(first.revisionTime || 0).getTime()
          );

        const historyWithDiff: HistoryItem[] = sortedHistory.map(
          (historyItem, index) => {
            const previousPayload = sortedHistory[index + 1]?.payload || {};
            return {
              uniqueKey: `${historyItem.id}-${historyItem.revisionTime}`,

              id: historyItem.id,
              revisionTime: historyItem.revisionTime,
              revisionType: historyItem.revisionType,
              payload: historyItem.payload,
              diff:
                index < sortedHistory.length - 1
                  ? this.buildDiff(previousPayload, historyItem.payload)
                  : [],
            };
          }
        );

        this.historyRows = historyWithDiff;
      } catch (e: any) {
        this.errorMessage = e?.message || 'Error';
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    localdialog(value: boolean) {
      if (value) this.fetchHistory();
    },
  },
});
